import { useCallback, useState } from 'react';
import {
  AUDIENCE_SEGMENT_MODES,
  HARD_EXCLUSION_REASONS,
  type AudienceFieldDescriptor,
  type FilterNode,
} from '@ferrari/types';
import {
  api,
  ApiError,
  type AudienceSourcesResponse,
  type AudienceSegmentsResponse,
  type BindingsResponse,
  type EnsaioDeEnvio,
  type FilterProblem,
  type PreviewEnvelope,
  type PreviewState,
} from '../lib/api';
import { useResource } from '../lib/useResource';
import { formatNumber } from '../lib/dataState';
import { formatInstant } from '../lib/format';
import { Confirmacao } from '../components/Confirmacao';
import { FilterBuilder } from '../components/FilterBuilder';
import { contarCondicoes, grupoInicial } from '../lib/filtro';

/**
 * Audience & Lists (Console SPEC v1.5 §177 a §185, §191 a §193).
 *
 * A tela onde se conecta fonte, se monta filtro e se salva segmento. Tres
 * regras que ela existe para nao quebrar:
 *
 * 1. **ausencia nunca vira zero.** Fonte sem conector, fonte nao aprovada e
 *    segmento nunca avaliado aparecem com o nome do estado e o que fazer. Sete
 *    zeros na previa seriam lidos como "o filtro nao casou ninguem", que e uma
 *    informacao completamente diferente de "ninguem foi perguntado";
 *
 * 2. **os numeros do §183 vem do servidor, do avaliador puro.** Nenhum deles e
 *    calculado aqui, nem somado, nem arredondado: numero que quem opera usa
 *    para decidir gasto com anuncio sai de funcao determinista, num lugar so;
 *
 * 3. **a validacao do filtro e do servidor.** A tela mostra os problemas que
 *    ele devolveu, com a frase de operacao. Uma segunda copia da regra aqui
 *    divergiria, e o sintoma seria a tela liberar o que o servidor recusa.
 */

const ROTULO_DO_ESTADO_DA_FONTE: Record<string, string> = {
  CONNECTED: 'conectada',
  DISCONNECTED: 'desconectada',
  DEGRADED: 'degradada',
  AUTH_REQUIRED: 'precisa renovar a credencial',
  ERROR: 'com erro',
};

const ROTULO_DA_APROVACAO: Record<string, string> = {
  APPROVED: 'aprovada',
  PAUSED: 'pausada',
  BLOCKED: 'bloqueada',
  REQUIRES_REVIEW: 'aguardando revisão',
};

const ROTULO_DO_MODO: Record<string, string> = {
  DYNAMIC: 'dinâmico (reexecuta o filtro na fonte)',
  SNAPSHOT: 'congelado (preserva a lista daquele momento)',
};

const ROTULO_DA_EXCLUSAO: Record<string, string> = {
  explicit_opt_out: 'pediu para sair',
  suppressed_or_deleted_record: 'registro suprimido ou apagado',
  policy_blocked_record: 'bloqueado por política ativa',
  invalid_matching_identifier: 'identificador inválido para casar pessoa',
};

const ROTULO_DA_CAPACIDADE: Record<string, string> = {
  query: 'filtrar',
  incremental_sync: 'entregar só o que mudou',
  list_contacts: 'listar contatos',
  list_companies: 'listar empresas',
  write_back: 'escrever de volta',
};

/** O que fazer, por estado de prévia ausente. */
const ACAO_POR_ESTADO: Record<Exclude<PreviewState, 'ready'>, string> = {
  no_connector: 'Nenhum número é exibido porque nada consegue ler registros desta fonte ainda.',
  source_not_approved: 'Ajuste a aprovação da fonte para poder consultá-la.',
  capability_missing: 'Declare a capacidade que falta na configuração da fonte.',
  source_error: 'A fonte recusou a consulta. O motivo está acima.',
};

type Carga = {
  readonly fontes: AudienceSourcesResponse;
  readonly segmentos: AudienceSegmentsResponse;
};

export function AudienceLists() {
  /**
   * Contador de recarga.
   *
   * Salvar sem reler deixaria a lista de segmentos mostrando o estado anterior,
   * e a pessoa concluiria que o salvamento falhou.
   */
  const [versao, setVersao] = useState(0);
  const recarregar = useCallback(() => setVersao((v) => v + 1), []);

  const recurso = useResource<Carga>(async () => {
    const [fontes, segmentos] = await Promise.all([api.audienceSources(), api.audienceSegments()]);
    return { fontes, segmentos };
  }, [versao]);

  if (recurso.kind === 'loading') {
    return (
      <div className="text-sm" aria-busy="true">
        <h1 className="text-2xl font-bold">Audience &amp; Lists</h1>
        <p className="mt-2 text-ink-muted">Carregando…</p>
      </div>
    );
  }

  if (recurso.kind === 'error') {
    return (
      <div className="max-w-3xl space-y-4 text-sm">
        <h1 className="text-2xl font-bold">Audience &amp; Lists</h1>
        <div className="mt-4 rounded border border-danger-border bg-danger-surface p-4">
          <p className="font-medium">Não foi possível carregar as fontes e os segmentos.</p>
          <p className="mt-1">{recurso.message}</p>
          <p className="mt-2 text-xs text-ink-faint">
            Nenhuma contagem aparece com zero: zero e &quot;não sabemos&quot; são coisas diferentes.
            Código: {recurso.code}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 text-sm">
      <header>
        <h1 className="text-2xl font-bold">Audience &amp; Lists</h1>
        <p className="mt-1 max-w-3xl text-ink-muted">
          Fontes de dados conectadas, montagem de filtro, segmentos salvos e o envio de audiência
          para a Meta. A importação de arquivo CSV ou XLSX ainda não entra aqui.
        </p>
      </header>

      <Fontes dados={recurso.data.fontes} />
      <Montagem fontes={recurso.data.fontes} aoSalvar={recarregar} />
      <Segmentos dados={recurso.data.segmentos} aoAtualizar={recarregar} />
      {/*
        Os vínculos buscam o PRÓPRIO recurso, como as telas vizinhas fazem: uma
        falha na leitura das audiências da Meta não pode derrubar as fontes e os
        segmentos, que é o que a pessoa veio ver.
      */}
      <Vinculos />
    </div>
  );
}

// ===========================================================================
// Vínculos com a Meta e o envio (§188 a §190, §260)
// ===========================================================================

/**
 * O botão que manda dado pessoal para a Meta.
 *
 * Duas decisões que este bloco existe para tornar verdadeiras:
 *
 * 1. **quem aciona é uma pessoa olhando uma tela, e não um job.** O caminho de
 *    sync existia provado desde a fase 6 e não tinha rota nem botão. Um job
 *    dispararia envio de contato sem ninguém ver quantos vão;
 *
 * 2. **a confirmação diz NÚMEROS.** Primeiro o ensaio conta quantos sairiam e
 *    quantos ficam de fora, por motivo; só depois aparece o botão que envia, e
 *    ele devolve ao servidor o número conferido. Se a base mudou nesse meio
 *    tempo, o servidor recusa e mostra os números novos em vez de mandar uma
 *    lista que ninguém viu.
 */
function Vinculos() {
  const [versao, setVersao] = useState(0);
  const recurso = useResource<BindingsResponse>(() => api.audienceBindings(), [versao]);
  const [ensaio, setEnsaio] = useState<EnsaioDeEnvio | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const aoSincronizar = () => setVersao((v) => v + 1);

  async function conferir(id: string) {
    setErro(null);
    setAviso(null);
    setEnsaio(null);
    setOcupado(true);
    try {
      setEnsaio(await api.audienceSyncPreview(id));
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não foi possível falar com a API.');
    } finally {
      setOcupado(false);
    }
  }

  async function enviar(id: string, quantos: number) {
    setErro(null);
    setOcupado(true);
    try {
      const r = await api.audienceSync(id, { confirmed_records: quantos });
      setAviso(
        r.already_existed
          ? 'Este envio já tinha sido feito com a mesma chave, e nada foi enviado de novo.'
          : `Envio ${r.status}: ${formatNumber(r.records_submitted)} registro(s) enviado(s) à Meta.`,
      );
      setEnsaio(null);
      aoSincronizar();
    } catch (e) {
      // A mensagem do servidor traz os números novos quando a base mudou. Um
      // texto genérico aqui apagaria justamente o que a pessoa precisa conferir.
      setErro(e instanceof ApiError ? e.message : 'Não foi possível falar com a API.');
    } finally {
      setOcupado(false);
    }
  }

  if (recurso.kind !== 'ready') {
    return (
      <section className="mt-8" aria-labelledby="vinculos">
        <h2 id="vinculos" className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
          Audiências na Meta
        </h2>
        {recurso.kind === 'loading' ? (
          <p className="mt-3 text-ink-muted">Carregando…</p>
        ) : (
          /*
            Erro NUNCA degrada para lista vazia: "nenhuma audiência vinculada" e
            "não consegui ler os vínculos" levam a ações opostas, e a segunda
            precisa dizer que ninguém deveria concluir nada sobre o envio.
          */
          <p className="mt-3 rounded border border-danger-border bg-danger-surface p-3">
            Não foi possível ler as audiências da Meta, então nada é afirmado sobre elas aqui.{' '}
            {recurso.message}
          </p>
        )}
      </section>
    );
  }

  const dados = recurso.data;

  return (
    <section className="mt-8" aria-labelledby="vinculos">
      <h2 id="vinculos" className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
        Audiências na Meta
      </h2>
      <p className="mt-1 text-xs text-ink-muted">
        Enviar uma lista para a Meta manda dado de pessoas para fora. Por isso o envio é sempre de
        alguém olhando esta tela, nunca de um processo automático, e a confirmação diz quantos vão e
        quantos ficam de fora.
      </p>

      {aviso === null ? null : (
        <p className="mt-2 rounded-[11px] border border-line bg-surface-sunken p-3 text-sm">
          {aviso}
        </p>
      )}
      {erro === null ? null : (
        <p className="mt-2 rounded border border-danger-border bg-danger-surface p-3 text-sm">
          {erro}
        </p>
      )}

      {dados.provider_configured ? null : (
        <p className="mt-3 rounded border border-warn-border bg-warn-surface p-3 text-sm">
          Não existe conexão com a Meta neste ambiente. Conferir os números continua funcionando;
          enviar está desligado, e nada sai por engano.
        </p>
      )}

      {dados.bindings.length === 0 ? (
        <p className="mt-3 rounded-[11px] border border-line bg-surface-sunken p-4 text-ink-muted">
          Nenhum segmento está vinculado a uma audiência da Meta. Sem vínculo não há para onde
          enviar, e por isso não existe contagem nenhuma aqui, nem zero.
        </p>
      ) : (
        <ul className="mt-3 space-y-3">
          {dados.bindings.map((v) => (
            <li
              key={v.binding_id}
              className="rounded-card border border-line bg-card p-4 shadow-card"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-medium">{v.audience_name}</span>
                <span className="text-xs text-ink-muted">{v.status_label}</span>
              </div>
              <p className="mt-1 text-xs text-ink-muted">
                Segmento {v.segment_name ?? 'não encontrado'} · conta {v.meta_ad_account_id}
              </p>
              <p className="mt-1 text-xs text-ink-muted">
                {v.never_synced ? (
                  <span className="text-ink-faint">nunca sincronizada</span>
                ) : (
                  `última sincronização em ${formatInstant(v.last_sync_at ?? '')}`
                )}
                {v.last_run === null
                  ? ''
                  : ` · ${formatNumber(v.last_run.records_submitted)} enviado(s), ${formatNumber(
                      v.last_run.records_excluded,
                    )} fora`}
              </p>

              {/*
                A última corrida com erro fica VISÍVEL com o código e a mensagem
                crua do provedor. É ela que transforma "não funcionou" em
                diagnóstico de dez segundos, e o motivo mais comum aqui (escopo,
                restrição de conta) não se resolve tentando de novo.
              */}
              {v.last_run !== null && v.last_run.error_code !== null ? (
                <p className="mt-2 rounded border border-danger-border bg-danger-surface p-2 text-xs">
                  Última corrida com erro ({v.last_run.error_code}): {v.last_run.error_message}
                </p>
              ) : null}

              {ensaio?.binding_id === v.binding_id ? (
                <Confirmacao
                  titulo={`Enviar "${v.audience_name}" para a Meta`}
                  oQueVaiAcontecer="Os registros abaixo sobem cifrados para a audiência da Meta. Quem está excluído não é enviado, e a conferência acontece de novo no servidor antes de sair."
                  numeros={[
                    {
                      rotulo: 'Vão para a Meta',
                      valor: formatNumber(ensaio.records_that_will_be_sent),
                      detalhe: 'pessoas',
                    },
                    {
                      rotulo: 'Ficam de fora',
                      valor: formatNumber(ensaio.records_excluded),
                      detalhe: 'exclusões e identificadores inúteis',
                    },
                    {
                      rotulo: 'Repetidos na origem',
                      valor: formatNumber(ensaio.records_duplicated),
                      detalhe: 'sobem uma vez só',
                    },
                    {
                      rotulo: 'Considerados no segmento',
                      valor: formatNumber(ensaio.records_selected),
                    },
                  ]}
                  textoDoBotao="Confirmar e enviar"
                  aoConfirmar={() => enviar(v.binding_id, ensaio.records_that_will_be_sent)}
                  aoCancelar={() => setEnsaio(null)}
                  ocupado={ocupado}
                  impedimento={
                    !dados.provider_configured
                      ? 'Não existe conexão com a Meta neste ambiente.'
                      : ensaio.records_that_will_be_sent === 0
                        ? 'Nenhum registro sobreviveria às exclusões: não há o que enviar.'
                        : null
                  }
                />
              ) : (
                <button
                  type="button"
                  onClick={() => conferir(v.binding_id)}
                  disabled={ocupado}
                  className="mt-2 rounded border border-line px-2 py-1 text-xs hover:bg-surface-raised disabled:opacity-50"
                >
                  Conferir quantos iriam para a Meta
                </button>
              )}

              {ensaio?.binding_id === v.binding_id ? (
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer text-ink-muted">
                    Quem fica de fora, por motivo
                  </summary>
                  <ul className="mt-1 list-disc pl-5">
                    {ensaio.excluded_by_reason.map((m) => (
                      <li key={m.reason}>
                        {m.label}: {formatNumber(m.count)}
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ===========================================================================
// Fontes
// ===========================================================================

function Fontes({ dados }: { dados: AudienceSourcesResponse }) {
  return (
    <section className="mt-6" aria-labelledby="fontes">
      <h2 id="fontes" className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
        Fontes de dados
      </h2>

      {dados.sources.length === 0 ? (
        <p className="mt-3 rounded-[11px] border border-line bg-surface-sunken p-4 text-ink-muted">
          Nenhuma fonte de audiência registrada. Sem fonte não há de onde tirar contato, e por isso
          não existe contagem nenhuma para mostrar, nem zero.
        </p>
      ) : (
        <ul className="mt-3 space-y-3">
          {dados.sources.map((f) => (
            <li
              key={f.source_id}
              className="rounded-card border border-line bg-card p-4 shadow-card"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-medium">{f.logical_source_name}</span>
                <span className="text-xs text-ink-muted">
                  {f.provider_name ?? 'fornecedor não configurado'}
                </span>
              </div>

              <p className="mt-1 text-xs text-ink-muted">
                Conexão: {ROTULO_DO_ESTADO_DA_FONTE[f.status] ?? f.status} · Aprovação:{' '}
                <strong>{ROTULO_DA_APROVACAO[f.approval_status] ?? f.approval_status}</strong> (
                {f.approval_scope === 'ENTIRE_SOURCE' ? 'fonte inteira' : 'recorte parcial'})
              </p>

              {/*
                Conector e um eixo PROPRIO, ao lado de conexão e aprovação: a
                fonte pode passar nos dois outros e ainda assim não ter código
                capaz de ler dela, e é isso que impede a prévia.
              */}
              {f.connector_installed ? (
                <p className="mt-1 text-xs text-ink-muted">Conector de leitura instalado.</p>
              ) : (
                <p className="mt-1 text-xs text-warn-ink">
                  Sem conector de leitura. A fonte está registrada, mas nada consegue trazer
                  registros dela ainda, então prévia e contagem não existem para ela.
                </p>
              )}

              <p className="mt-2 text-xs text-ink-muted">
                Sabe:{' '}
                {Object.entries(f.capabilities)
                  .filter(([, ligada]) => ligada)
                  .map(([nome]) => ROTULO_DA_CAPACIDADE[nome] ?? nome)
                  .join(', ') || 'nada declarado (capacidade não declarada vale como indisponível)'}
              </p>

              <p className="mt-1 text-xs text-ink-muted">
                Exclusões sempre aplicadas:{' '}
                {HARD_EXCLUSION_REASONS.filter((m) => f.hard_exclusions[m])
                  .map((m) => ROTULO_DA_EXCLUSAO[m])
                  .join(', ') || 'nenhuma ligada'}
              </p>

              <p className="mt-1 text-xs text-ink-faint">
                {formatNumber(f.available_fields.length)} campo(s) declarado(s) ·{' '}
                {formatNumber(f.segment_count)} segmento(s) ·{' '}
                {f.last_sync_at === null
                  ? 'nunca sincronizada'
                  : `última sincronização em ${formatInstant(f.last_sync_at)}`}
              </p>

              {f.discarded_fields > 0 ? (
                <p className="mt-1 text-xs text-danger-ink">
                  {formatNumber(f.discarded_fields)} entrada(s) do catálogo de campos estavam fora
                  do formato e não aparecem no montador de filtro. Corrija a configuração da fonte.
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ===========================================================================
// Montagem do segmento
// ===========================================================================

type ResultadoDeSalvar =
  | { kind: 'salvo'; nome: string }
  | { kind: 'erro'; mensagem: string }
  | { kind: 'filtro_invalido'; problemas: FilterProblem[] };

function Montagem({ fontes, aoSalvar }: { fontes: AudienceSourcesResponse; aoSalvar: () => void }) {
  const [fonteId, setFonteId] = useState(fontes.sources[0]?.source_id ?? '');
  const fonte = fontes.sources.find((f) => f.source_id === fonteId) ?? fontes.sources[0];

  const campos: readonly AudienceFieldDescriptor[] = (fonte?.available_fields ?? []).map((c) => ({
    name: c.name,
    label: c.label,
    type: c.type as AudienceFieldDescriptor['type'],
    filterable: c.filterable,
    usableForMatching: c.usable_for_matching,
  }));

  /**
   * A fonte nao declarou nenhum campo filtravel.
   *
   * Quando isso acontece, os dois botoes ficam DESLIGADOS. Sem isso, a tela
   * mandaria ao servidor um filtro sobre o nome de campo de reserva, que a
   * fonte nao reconhece: a previa voltaria com zero e o segmento salvo nunca
   * selecionaria ninguem, os dois sem nenhuma explicacao.
   */
  const semCampoFiltravel = !campos.some((c) => c.filterable);

  const primeiroFiltravel = campos.find((c) => c.filterable)?.name ?? 'company';
  const [filtro, setFiltro] = useState<FilterNode>(() => grupoInicial(primeiroFiltravel));
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [modo, setModo] = useState<string>('DYNAMIC');

  const [previa, setPrevia] = useState<PreviewEnvelope | null>(null);
  const [problemas, setProblemas] = useState<FilterProblem[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [resultado, setResultado] = useState<ResultadoDeSalvar | null>(null);

  if (fonte === undefined) {
    return (
      <section className="mt-8" aria-labelledby="montagem">
        <h2 id="montagem" className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
          Montar segmento
        </h2>
        <p className="mt-3 rounded-[11px] border border-line bg-surface-sunken p-4 text-ink-muted">
          Sem fonte conectada não há campo para filtrar. Registre uma fonte de audiência antes de
          montar um segmento.
        </p>
      </section>
    );
  }

  async function verPrevia() {
    setErro(null);
    setProblemas([]);
    setResultado(null);
    setOcupado(true);
    try {
      const r = await api.audiencePreview({ source_id: fonte!.source_id, filter: filtro });
      setPrevia(r);
    } catch (e) {
      tratarErro(e, setErro, setProblemas);
      setPrevia(null);
    } finally {
      setOcupado(false);
    }
  }

  async function salvar(evento: React.FormEvent) {
    evento.preventDefault();
    setErro(null);
    setProblemas([]);
    setResultado(null);

    if (nome.trim().length === 0) {
      setErro('Dê um nome ao segmento. É por ele que alguém vai escolher esta lista depois.');
      return;
    }

    setOcupado(true);
    try {
      const r = await api.audienceSaveSegment({
        name: nome.trim(),
        description: descricao.trim() === '' ? undefined : descricao.trim(),
        source_id: fonte!.source_id,
        mode: modo,
        filter: filtro,
      });
      setPrevia({ preview_state: r.preview_state, reason: r.reason, preview: r.preview });
      setResultado({ kind: 'salvo', nome: r.segment.name });
      setNome('');
      setDescricao('');
      // Relê a lista: salvar sem reler deixaria o segmento novo invisível, e a
      // pessoa tentaria salvar de novo e receberia "nome já usado".
      aoSalvar();
    } catch (e) {
      tratarErro(e, setErro, setProblemas);
    } finally {
      setOcupado(false);
    }
  }

  return (
    <section className="mt-8" aria-labelledby="montagem">
      <h2 id="montagem" className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
        Montar segmento
      </h2>
      <p className="mt-1 text-xs text-ink-muted">
        O segmento conta CONTATOS. Os números da prévia falam de e-mail, telefone e envio para a
        Meta, que são pessoas.
      </p>

      <form onSubmit={salvar} className="mt-3 space-y-3">
        <div className="flex flex-wrap gap-3">
          <div>
            <label htmlFor="fonte-do-segmento" className="block text-xs text-ink-muted">
              Fonte
            </label>
            <select
              id="fonte-do-segmento"
              value={fonte.source_id}
              onChange={(e) => {
                setFonteId(e.target.value);
                setPrevia(null);
                setResultado(null);
              }}
              className="mt-1 rounded border border-line px-2 py-1 text-sm"
            >
              {fontes.sources.map((f) => (
                <option key={f.source_id} value={f.source_id}>
                  {f.logical_source_name}
                </option>
              ))}
            </select>
          </div>

          <div className="grow">
            <label htmlFor="nome-do-segmento" className="block text-xs text-ink-muted">
              Nome do segmento
            </label>
            <input
              id="nome-do-segmento"
              value={nome}
              autoComplete="off"
              onChange={(e) => setNome(e.target.value)}
              className="mt-1 w-full rounded border border-line px-2 py-1 text-sm"
            />
          </div>

          <div>
            <label htmlFor="modo-do-segmento" className="block text-xs text-ink-muted">
              Modo
            </label>
            <select
              id="modo-do-segmento"
              value={modo}
              onChange={(e) => setModo(e.target.value)}
              className="mt-1 rounded border border-line px-2 py-1 text-sm"
            >
              {AUDIENCE_SEGMENT_MODES.map((m) => (
                <option key={m} value={m}>
                  {ROTULO_DO_MODO[m]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="descricao-do-segmento" className="block text-xs text-ink-muted">
            Descrição (opcional)
          </label>
          <input
            id="descricao-do-segmento"
            value={descricao}
            autoComplete="off"
            onChange={(e) => setDescricao(e.target.value)}
            className="mt-1 w-full rounded border border-line px-2 py-1 text-sm"
          />
        </div>

        <FilterBuilder filtro={filtro} campos={campos} onChange={setFiltro} />

        <p className="text-xs text-ink-faint">
          {formatNumber(contarCondicoes(filtro))} condição(ões) no filtro.
        </p>

        {semCampoFiltravel ? (
          <p className="rounded border border-warn-border bg-warn-surface p-3 text-sm">
            Prévia e salvamento estão desligados enquanto a fonte não tiver campo filtrável. Um
            segmento montado assim nunca selecionaria ninguém.
          </p>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={verPrevia}
            disabled={ocupado || semCampoFiltravel}
            className="rounded border border-line-strong px-3 py-1 text-sm hover:bg-surface-raised disabled:opacity-50"
          >
            Ver prévia
          </button>
          <button
            type="submit"
            disabled={ocupado || semCampoFiltravel}
            className="rounded border border-accent-border bg-accent-strong px-3 py-1 text-sm text-white disabled:opacity-50"
          >
            Salvar segmento
          </button>
        </div>
      </form>

      {erro === null ? null : (
        <p className="mt-3 rounded border border-danger-border bg-danger-surface p-3 text-sm">
          {erro}
        </p>
      )}

      {problemas.length === 0 ? null : (
        <div className="mt-3 rounded border border-danger-border bg-danger-surface p-3">
          <p className="text-sm font-medium">O filtro precisa de ajuste antes de continuar.</p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
            {problemas.map((p) => (
              <li key={`${p.path}-${p.code}`}>
                {p.what_to_do}{' '}
                <span className="text-xs text-ink-faint">
                  ({p.path}: {p.detail})
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {resultado?.kind === 'salvo' ? (
        <p className="mt-3 rounded-[11px] border border-line bg-surface-sunken p-3 text-sm">
          Segmento &quot;{resultado.nome}&quot; salvo.
        </p>
      ) : null}

      {previa === null ? null : <Previa envelope={previa} />}
    </section>
  );
}

function tratarErro(
  e: unknown,
  setErro: (m: string | null) => void,
  setProblemas: (p: FilterProblem[]) => void,
) {
  if (!(e instanceof ApiError)) {
    setErro('Não foi possível falar com a API do plano de controle.');
    return;
  }
  setErro(e.message);
  setProblemas(problemasDoErro(e));
}

/**
 * Os problemas de filtro que vieram no corpo da recusa.
 *
 * Lidos do corpo cru que `ApiError` carrega, e não por um segundo `fetch`: a
 * extração de erro vive em um lugar só, e uma segunda cópia dela é como o bug
 * sobrevive ao próprio conserto. Corpo sem a lista devolve vazio, sem inventar
 * problema nenhum.
 */
function problemasDoErro(e: ApiError): FilterProblem[] {
  const corpo = (e.body ?? {}) as { problems?: unknown };
  return Array.isArray(corpo.problems) ? (corpo.problems as FilterProblem[]) : [];
}

// ===========================================================================
// A prévia do §183
// ===========================================================================

export function Previa({ envelope }: { envelope: PreviewEnvelope }) {
  if (envelope.preview === null || envelope.preview_state !== 'ready') {
    const estado = envelope.preview_state as Exclude<PreviewState, 'ready'>;
    return (
      <div className="mt-3 rounded border border-warn-border bg-warn-surface p-3 text-sm">
        <p className="font-medium">Sem prévia.</p>
        <p className="mt-1">{envelope.reason ?? 'A fonte não respondeu.'}</p>
        <p className="mt-1 text-xs text-ink-muted">{ACAO_POR_ESTADO[estado] ?? ''}</p>
      </div>
    );
  }

  const p = envelope.preview;

  return (
    <div className="mt-3 rounded border border-line p-3">
      <h3 className="text-sm font-semibold">Prévia</h3>
      <dl className="mt-2 grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">
        <Numero rotulo="Registros no total" valor={p.total_records} />
        <Numero rotulo="Com e-mail" valor={p.records_with_email} />
        <Numero rotulo="Com telefone" valor={p.records_with_phone} />
        <Numero rotulo="Com os dois" valor={p.records_with_both} />
        <Numero rotulo="Elegíveis após exclusões" valor={p.eligible_after_hard_exclusions} />
        <Numero rotulo="Iriam para a Meta" valor={p.estimated_records_for_meta_upload} />
      </dl>

      <p className="mt-2 text-xs text-ink-muted">
        Última atualização na fonte:{' '}
        {p.last_source_update_at === null ? (
          <span className="text-ink-faint">a fonte não informa a data</span>
        ) : (
          formatInstant(p.last_source_update_at)
        )}
      </p>

      <h4 className="mt-3 text-xs font-semibold uppercase tracking-wide">Excluídos por motivo</h4>
      <div className="mt-1 overflow-x-auto">
        <table className="w-full min-w-[28rem] border-collapse text-xs">
          <caption className="sr-only">Registros excluídos por motivo</caption>
          <thead>
            <tr className="border-b border-line text-left">
              <th scope="col" className="py-1 pr-3 font-semibold">
                Motivo
              </th>
              <th scope="col" className="py-1 pr-3 font-semibold">
                A fonte afirmou
              </th>
              <th scope="col" className="py-1 font-semibold">
                A fonte não soube dizer
              </th>
            </tr>
          </thead>
          <tbody>
            {HARD_EXCLUSION_REASONS.map((motivo) => {
              const contagem = p.excluded_by_reason[motivo] ?? { marked: 0, unknown: 0 };
              return (
                <tr key={motivo} className="border-b border-line">
                  <th scope="row" className="py-1 pr-3 font-normal">
                    {ROTULO_DA_EXCLUSAO[motivo]}
                  </th>
                  <td className="py-1 pr-3">{formatNumber(contagem.marked)}</td>
                  <td className="py-1">{formatNumber(contagem.unknown)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-1 text-xs text-ink-faint">
        As duas colunas ficam separadas porque levam a ações diferentes: a primeira é a regra
        funcionando, a segunda é um campo que falta na fonte.
      </p>
    </div>
  );
}

function Numero({ rotulo, valor }: { rotulo: string; valor: number }) {
  return (
    <div>
      <dt className="text-xs text-ink-muted">{rotulo}</dt>
      <dd>
        <strong>{formatNumber(valor)}</strong>
      </dd>
    </div>
  );
}

// ===========================================================================
// Segmentos salvos
// ===========================================================================

function Segmentos({
  dados,
  aoAtualizar,
}: {
  dados: AudienceSegmentsResponse;
  aoAtualizar: () => void;
}) {
  const [emCurso, setEmCurso] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  async function reexecutar(id: string) {
    setAviso(null);
    setEmCurso(id);
    try {
      const r = await api.audienceRefreshSegment(id);
      setAviso(
        r.refreshed
          ? 'Segmento reexecutado. Os números abaixo são os novos.'
          : (r.reason ?? 'A fonte não respondeu, e os números abaixo continuam sendo os antigos.'),
      );
      aoAtualizar();
    } catch (e) {
      setAviso(
        e instanceof ApiError
          ? e.message
          : 'Não foi possível falar com a API do plano de controle.',
      );
    } finally {
      setEmCurso(null);
    }
  }

  return (
    <section className="mt-8" aria-labelledby="segmentos">
      <h2 id="segmentos" className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
        Segmentos salvos
      </h2>

      {aviso === null ? null : (
        <p className="mt-2 rounded-[11px] border border-line bg-surface-sunken p-3 text-sm">
          {aviso}
        </p>
      )}

      {dados.segments.length === 0 ? (
        <p className="mt-3 rounded-[11px] border border-line bg-surface-sunken p-4 text-ink-muted">
          Nenhum segmento salvo ainda. Monte um filtro acima e salve para reutilizá-lo.
        </p>
      ) : (
        <ul className="mt-3 space-y-3">
          {dados.segments.map((s) => (
            <li
              key={s.segment_id}
              className="rounded-card border border-line bg-card p-4 shadow-card"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-medium">{s.name}</span>
                <span className="text-xs text-ink-muted">
                  {s.logical_source_name ?? 'fonte não identificada'}
                </span>
              </div>

              {s.description === null ? null : (
                <p className="mt-1 text-xs text-ink-muted">{s.description}</p>
              )}

              <p className="mt-1 text-xs text-ink-muted">
                Modo: {ROTULO_DO_MODO[s.mode] ?? s.mode}
              </p>

              <dl className="mt-2 grid grid-cols-2 gap-2 text-xs">
                <div>
                  <dt className="text-ink-muted">Registros na última execução</dt>
                  <dd>
                    {s.record_count_last_run === null ? (
                      <span className="text-ink-faint">nunca avaliado (não é zero)</span>
                    ) : (
                      <strong>{formatNumber(s.record_count_last_run)}</strong>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-ink-muted">Elegíveis após exclusões</dt>
                  <dd>
                    {s.eligible_record_count === null ? (
                      <span className="text-ink-faint">nunca avaliado (não é zero)</span>
                    ) : (
                      <strong>{formatNumber(s.eligible_record_count)}</strong>
                    )}
                  </dd>
                </div>
              </dl>

              <p className="mt-2 text-xs text-ink-faint">
                Criado por {s.created_by} em {formatInstant(s.created_at)} ·{' '}
                {s.last_refreshed_at === null
                  ? 'nunca reexecutado'
                  : `reexecutado em ${formatInstant(s.last_refreshed_at)}`}
              </p>

              {s.filter_problems.length === 0 ? null : (
                <div className="mt-2 rounded border border-danger-border bg-danger-surface p-2 text-xs">
                  <p className="font-medium">
                    O filtro gravado não é mais válido, e reexecutar traria uma contagem errada.
                  </p>
                  <ul className="mt-1 list-disc pl-5">
                    {s.filter_problems.map((p) => (
                      <li key={`${p.path}-${p.code}`}>{p.what_to_do}</li>
                    ))}
                  </ul>
                </div>
              )}

              <button
                type="button"
                onClick={() => reexecutar(s.segment_id)}
                disabled={emCurso === s.segment_id}
                className="mt-2 rounded border border-line px-2 py-1 text-xs hover:bg-surface-raised disabled:opacity-50"
              >
                Reexecutar filtro na fonte
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
